// Centralised logging for the admin panel.
//
// Why this exists: caught errors in the service layer used to `console.error`
// and stop there — they never reached Sentry, so real backend failures were
// invisible in production telemetry. Route them through `logger` instead:
//   - development → logged to the console exactly as before (dev-gated), and
//   - production  → forwarded to Sentry (captureException / captureMessage).
//
// Sentry is a no-op when VITE_SENTRY_DSN is unset (see main.tsx), so these
// helpers are always safe to call regardless of environment or config.
import * as Sentry from '@sentry/react'

const isDev = import.meta.env.DEV

type LogContext = Record<string, unknown>

/**
 * Normalise an unknown throwable into an Error so Sentry gets a real
 * exception (with a stack) rather than a stringified blob.
 */
function toError(message: string, cause: unknown): Error {
  if (cause instanceof Error) return cause
  if (cause === undefined || cause === null) return new Error(message)
  return new Error(`${message}: ${String(cause)}`)
}

export const logger = {
  /**
   * A handled error (typically in a catch block). Reaches Sentry in
   * production; logs to the console in development.
   */
  error(message: string, cause?: unknown, context?: LogContext): void {
    if (isDev) console.error(message, cause ?? '', context ?? '')
    Sentry.captureException(toError(message, cause), {
      extra: { logMessage: message, ...(context ?? {}) },
    })
  },

  /**
   * A noteworthy but non-fatal condition worth surfacing in telemetry.
   */
  warn(message: string, context?: LogContext): void {
    if (isDev) console.warn(message, context ?? '')
    Sentry.captureMessage(message, { level: 'warning', extra: context })
  },

  /**
   * A development-only diagnostic. Never sent to telemetry.
   */
  info(message: string, context?: LogContext): void {
    // eslint-disable-next-line no-console -- logger is the sanctioned console sink
    if (isDev) console.info(message, context ?? '')
  },
}
